import { EntityForm } from '@lbcamden/lib-react';
import { FunctionComponent } from 'react';

import { usePcfraReferrer, useUserStore } from '~/client/hooks/storeHooks';
import { PcfraPerson, PcfraStatus } from '~/common/types/people';

import { CurrentResidentsDialog } from '../CurrentResidents.styled';
import { getPersonTitleAndSurname } from '../helpers';
import { useHandlePcfraActivity } from '../useHandlePcfraActivity';
import { formConfig, formSchema, RecordAssessmentFormValues } from './config';

type Props = {
  open?: boolean;
  person?: PcfraPerson;
  onClose(): void;
};

export const RecordAssessmentDialog: FunctionComponent<Props> = ({ open, person, onClose }) => {
  const userStore = useUserStore();
  const referrer = usePcfraReferrer(person?.personId || '');
  const handlePcfraActivity = useHandlePcfraActivity();

  const title = `Record assessment for ${getPersonTitleAndSurname(person)}`;

  const onSave = ({
    pcfraNeeded,
    referrerEmail,
    note,
    pcfraReviewDate,
  }: RecordAssessmentFormValues) => {
    if (!person) {
      return;
    }
    const { propertyId, personId } = person;

    const newPcfraStatus = pcfraNeeded === 'Yes' ? PcfraStatus.Necessary : PcfraStatus.Unnecessary;
    const hasNewReferrer = !!referrerEmail && referrerEmail !== referrer?.emailAddress;
    const removedReferrer = !referrerEmail && !!referrer;

    userStore?.store.transaction(() => {
      if (hasNewReferrer) {
        userStore?.handleAndDispatchMessage('updatePcfraReferrer', personId, {
          propertyId,
          personId,
          emailAddress: referrerEmail,
        });
      } else if (removedReferrer) {
        userStore?.handleAndDispatchMessage('deletePcfraReferrer', personId, {
          propertyId,
          personId,
        });
      }
      handlePcfraActivity({
        propertyId,
        personId,
        newPcfraStatus,
        note,
        pcfraReviewDate: pcfraReviewDate || null,
        shouldNotifyReferrer: !!referrerEmail,
      });
    });

    onClose();
  };

  return (
    <CurrentResidentsDialog open={open} title={title} accessibleInfo={{ title }} onClose={onClose}>
      <EntityForm<RecordAssessmentFormValues>
        inDialog
        schema={formSchema}
        config={formConfig}
        initialValue={{
          referrerEmail: referrer?.emailAddress,
          pcfraReviewDate: person?.pcfraReviewDate,
        }}
        onCancel={onClose}
        onSave={onSave}
      />
    </CurrentResidentsDialog>
  );
};
